"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PrintReport = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_components_1 = __importDefault(require("styled-components"));
const Ahti_logo_png_1 = __importDefault(require("../../../images/Ahti_logo.png"));
const format_1 = require("./format");
const PrintCharts_1 = require("./PrintCharts");
const model_1 = require("./model");
/**
 * A4 report, hidden on screen and revealed only for printing. Built separately
 * from the interactive UI rather than restyled out of it: the screen layout is
 * two columns of controls, and a printed record wants the inputs written out as
 * a reference table beside the figures they produced.
 *
 * Deliberately free of background fills — browsers drop those by default when
 * printing, so anything that depends on them prints wrong.
 */
const Sheet = styled_components_1.default.div `
  display: none;

  @media print {
    display: block;
    font-family: "Noto Sans", sans-serif;
    color: #000;
    font-size: 9.5pt;
    line-height: 1.35;
  }
`;
/** Everything that is not part of working out the value moves to page two. */
const Page = styled_components_1.default.div `
  break-after: page;

  &:last-child {
    break-after: auto;
  }
`;
const ContinuationHead = styled_components_1.default.header `
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8mm;
  border-bottom: 0.75pt solid #092960;
  padding-bottom: 2mm;
  margin-bottom: 4mm;
  font-size: 8.5pt;
  color: #36498d;

  b {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-weight: 800;
    font-size: 11pt;
    color: #092960;
  }
`;
const Lead = styled_components_1.default.p `
  margin: 0 0 3mm;
  font-size: 9pt;
  color: #092960;
`;
const Masthead = styled_components_1.default.header `
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12mm;
  border-bottom: 1.5pt solid #092960;
  padding-bottom: 2.5mm;
  margin-bottom: 4mm;

  img {
    width: 34mm;
    height: auto;
  }
`;
const Kicker = styled_components_1.default.p `
  margin: 0 0 1mm;
  font-size: 7.5pt;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #36498d;
`;
const Title = styled_components_1.default.h1 `
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 800;
  font-size: 15pt;
  line-height: 1.12;
  margin: 0;
  color: #092960;
`;
const Prepared = styled_components_1.default.p `
  margin: 1.5mm 0 0;
  font-size: 8.5pt;
  color: #36498d;
`;
const Section = styled_components_1.default.section `
  margin-bottom: 3mm;
  break-inside: avoid;
`;
const SectionTitle = styled_components_1.default.h2 `
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 800;
  font-size: 9pt;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #36498d;
  margin: 0 0 1.5mm;
  padding-bottom: 0.8mm;
  border-bottom: 0.5pt solid #a5abb0;
`;
const Rows = styled_components_1.default.table `
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;

  td {
    padding: 1.1mm 0;
    vertical-align: baseline;
    border-bottom: 0.25pt solid #d1d4d7;
  }

  td:last-child {
    text-align: right;
    white-space: nowrap;
  }

  tr:last-child td {
    border-bottom: 0;
  }
`;
const Note = styled_components_1.default.span `
  display: block;
  font-size: 8pt;
  color: #36498d;
`;
const Headline = styled_components_1.default.div `
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8mm;
  border: 0.75pt solid #092960;
  padding: 2.5mm 4mm;
  margin-bottom: 4mm;
  break-inside: avoid;

  span {
    font-size: 8.5pt;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #36498d;
  }

  b {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-weight: 800;
    font-size: 18pt;
    color: #092960;
  }
`;
const RouteHead = styled_components_1.default.tr `
  td {
    padding-top: 2.5mm;
    font-size: 8pt;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #36498d;
    border-bottom: 0.5pt solid #a5abb0;
  }
`;
const Net = styled_components_1.default.tr `
  td {
    font-weight: 700;
    border-bottom: 0.75pt solid #092960;
  }
`;
const Basis = styled_components_1.default.ul `
  margin: 0;
  padding-left: 4mm;
  font-size: 8pt;
  line-height: 1.3;
  color: #36498d;

  li {
    margin-bottom: 0.6mm;
  }
`;
const Footer = styled_components_1.default.footer `
  margin-top: 4mm;
  padding-top: 1.5mm;
  border-top: 0.5pt solid #a5abb0;
  font-size: 7.5pt;
  color: #36498d;
`;
const usd = (value) => (value < 0 ? "−$" : "$") + (0, format_1.formatInt)(Math.abs(value));
const PrintReport = ({ inputs, result, }) => {
    const prepared = new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
    return ((0, jsx_runtime_1.jsxs)(Sheet, { "data-print": "report", children: [(0, jsx_runtime_1.jsxs)(Page, { children: [(0, jsx_runtime_1.jsxs)(Masthead, { children: [(0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)(Kicker, { children: "FuelEU Maritime 2026 \u00B7 pooling supply side" }), (0, jsx_runtime_1.jsx)(Title, { children: "Compliance surplus calculation" }), (0, jsx_runtime_1.jsxs)(Prepared, { children: ["Prepared ", prepared] })] }), (0, jsx_runtime_1.jsx)("img", { src: Ahti_logo_png_1.default, alt: "Ahti Climate" })] }), (0, jsx_runtime_1.jsxs)(Headline, { children: [(0, jsx_runtime_1.jsx)("span", { children: "Compliance surplus generated" }), (0, jsx_runtime_1.jsxs)("b", { children: [(0, format_1.formatInt)(Math.max(result.surplus, 0)), " tCO\u2082e"] })] }), (0, jsx_runtime_1.jsxs)(Section, { children: [(0, jsx_runtime_1.jsx)(SectionTitle, { children: "The figures this is based on" }), (0, jsx_runtime_1.jsx)(Rows, { children: (0, jsx_runtime_1.jsxs)("tbody", { children: [(0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsxs)("td", { children: ["Fossil fuel replaced", (0, jsx_runtime_1.jsxs)(Note, { children: [(0, format_1.formatTwo)(result.fuel.ci), " gCO\u2082e/MJ well-to-wake \u00B7", " ", (0, format_1.formatTwo)(result.fuel.ef), " tCO\u2082 per tonne"] })] }), (0, jsx_runtime_1.jsx)("td", { children: result.fuel.name })] }), (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("td", { children: "Tonnes replaced" }), (0, jsx_runtime_1.jsxs)("td", { children: [(0, format_1.formatInt)(inputs.tons), " t"] })] }), (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsxs)("td", { children: ["Biofuel GHG intensity", (0, jsx_runtime_1.jsx)(Note, { children: "Certified well-to-wake value" })] }), (0, jsx_runtime_1.jsxs)("td", { children: [(0, format_1.formatOne)(inputs.ciBio), " gCO\u2082e/MJ"] })] }), (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("td", { children: "Fossil fuel price" }), (0, jsx_runtime_1.jsxs)("td", { children: ["$", (0, format_1.formatInt)(inputs.priceFossil), " / t"] })] }), (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("td", { children: "Biofuel price, delivered" }), (0, jsx_runtime_1.jsxs)("td", { children: ["$", (0, format_1.formatInt)(inputs.priceBio), " / t"] })] }), (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("td", { children: "EUA price" }), (0, jsx_runtime_1.jsxs)("td", { children: ["\u20AC", (0, format_1.formatTwo)(inputs.eua), " / t"] })] }), (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("td", { children: "Pooled unit price" }), (0, jsx_runtime_1.jsxs)("td", { children: ["\u20AC", (0, format_1.formatInt)(inputs.poolPriceEur), " / tCO\u2082e"] })] }), (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("td", { children: "EUR / USD" }), (0, jsx_runtime_1.jsx)("td", { children: (0, format_1.formatRate)(inputs.fx) })] })] }) })] }), (0, jsx_runtime_1.jsxs)(Section, { children: [(0, jsx_runtime_1.jsx)(SectionTitle, { children: "What the switch is worth" }), (0, jsx_runtime_1.jsx)(Rows, { children: (0, jsx_runtime_1.jsxs)("tbody", { children: [(0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsxs)("td", { children: ["EU ETS cost avoided", (0, jsx_runtime_1.jsx)(Note, { children: "Earned either way, on the fuel itself" })] }), (0, jsx_runtime_1.jsx)("td", { children: usd(result.ets) })] }), (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsx)("td", { children: "Biofuel premium paid" }), (0, jsx_runtime_1.jsx)("td", { children: usd(-result.premium) })] }), (0, jsx_runtime_1.jsx)(RouteHead, { children: (0, jsx_runtime_1.jsxs)("td", { colSpan: 2, children: ["Then the surplus \u2014 ", (0, format_1.formatInt)(Math.max(result.surplus, 0)), " ", "tCO\u2082e, one route or the other"] }) }), (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsxs)("td", { children: ["Profit from pooling the compliance", (0, jsx_runtime_1.jsxs)(Note, { children: ["At \u20AC", (0, format_1.formatInt)(inputs.poolPriceEur), " per tCO\u2082e"] })] }), (0, jsx_runtime_1.jsx)("td", { children: usd(result.poolProfit) })] }), (0, jsx_runtime_1.jsxs)(Net, { children: [(0, jsx_runtime_1.jsx)("td", { children: "Net result, pooling the surplus" }), (0, jsx_runtime_1.jsx)("td", { children: usd(result.poolNet) })] }), (0, jsx_runtime_1.jsxs)("tr", { children: [(0, jsx_runtime_1.jsxs)("td", { children: ["FuelEU penalty avoided", (0, jsx_runtime_1.jsxs)(Note, { children: ["Offsetting your own deficit at $", (0, format_1.formatInt)(result.penaltyRate), " per tCO\u2082e"] })] }), (0, jsx_runtime_1.jsx)("td", { children: usd(result.penaltyAvoided) })] }), (0, jsx_runtime_1.jsxs)(Net, { children: [(0, jsx_runtime_1.jsx)("td", { children: "Net result, offsetting your own penalty" }), (0, jsx_runtime_1.jsx)("td", { children: usd(result.offsetNet) })] })] }) })] }), (0, jsx_runtime_1.jsxs)(Section, { children: [(0, jsx_runtime_1.jsx)(SectionTitle, { children: "Where the value sits" }), (0, jsx_runtime_1.jsx)(PrintCharts_1.ValueBars, { result: result })] }), (0, jsx_runtime_1.jsxs)(Section, { children: [(0, jsx_runtime_1.jsx)(SectionTitle, { children: "Net result at other biofuel intensities" }), (0, jsx_runtime_1.jsx)(PrintCharts_1.SensitivityChart, { inputs: inputs })] })] }), (0, jsx_runtime_1.jsxs)(Page, { children: [(0, jsx_runtime_1.jsxs)(ContinuationHead, { children: [(0, jsx_runtime_1.jsx)("b", { children: "Basis, scope and disclaimer" }), (0, jsx_runtime_1.jsxs)("span", { children: ["Compliance surplus calculation \u00B7 ", prepared] })] }), (0, jsx_runtime_1.jsx)(Lead, { children: "The figures on the previous page depend on assumptions that rarely hold in full. This page records them so the calculation can be checked rather than taken on trust." }), (0, jsx_runtime_1.jsxs)(Section, { children: [(0, jsx_runtime_1.jsx)(SectionTitle, { children: "Your actual benefit depends on your FuelEU exposure" }), (0, jsx_runtime_1.jsxs)(Basis, { children: [(0, jsx_runtime_1.jsx)("li", { children: "A surplus only arises on energy inside the FuelEU scope: all energy on intra-EEA voyages and at berth in EEA ports, half of the energy on voyages to or from a port outside the EEA, and none beyond that. If part of the fuel is burned outside, both the surplus and the ETS saving fall proportionally." }), (0, jsx_runtime_1.jsx)("li", { children: "The surplus is netted against your own position first. Whatever covers a deficit inside your own reporting perimeter pays off as penalties avoided rather than as units sold, and only the remainder can be pooled." }), (0, jsx_runtime_1.jsx)("li", { children: "Final volumes are whatever your verifier confirms and THETIS-MRV records. Nothing can be pooled before that." })] })] }), (0, jsx_runtime_1.jsxs)(Section, { children: [(0, jsx_runtime_1.jsx)(SectionTitle, { children: "Basis and assumptions" }), (0, jsx_runtime_1.jsxs)(Basis, { children: [(0, jsx_runtime_1.jsxs)("li", { children: ["Compliance balance follows FuelEU Maritime Annex IV: (target intensity \u2212 biofuel intensity) \u00D7 energy delivered in scope. The 2026 target is ", (0, format_1.formatTwo)(model_1.TARGET_INTENSITY), " gCO\u2082e/MJ."] }), (0, jsx_runtime_1.jsx)("li", { children: "Biofuel replaces the fossil fuel one tonne for one tonne at 37 MJ/kg, and is assumed zero-rated for ETS at full phase-in." }), (0, jsx_runtime_1.jsxs)("li", { children: ["The surplus is valued either as penalty avoided at \u20AC", (0, format_1.formatInt)(model_1.PENALTY_EUR_PER_TONNE), " per tCO\u2082e or at the pooled unit price \u2014 never both, since the same tonnes cannot do both."] }), (0, jsx_runtime_1.jsx)("li", { children: "No RFNBO multiplier or wind reward factor is applied." }), (0, jsx_runtime_1.jsx)("li", { children: "The EUA figure is taken from the SparkChange Physical Carbon EUA ETC, which is backed by held allowances and quoted in euros. It tracks the allowance price closely but is not the ICE front-month settlement. FX is the ECB reference rate. Both were current when this sheet was prepared and can be overridden in the calculator." })] })] }), (0, jsx_runtime_1.jsx)(Footer, { children: "Indicative figures only and not an offer. Unit prices are quoted case by case. Prepared with the Ahti Climate surplus calculator \u2014 \u00A9 Ahti Climate" })] })] }));
};
exports.PrintReport = PrintReport;
